import { FieldPolicy, Reference, StoreValue } from '@apollo/client';

/**
 * This code comes from apollo itself
 * commenting out couple of warning / error shown
 */
type KeyArgs = FieldPolicy<any>['keyArgs'];

type TInternalRelay<TNode> = Readonly<{
	edges: Array<{
		cursor: string;
		node: TNode;
	}>;
	pageInfo: Readonly<{
		hasPreviousPage: boolean;
		hasNextPage: boolean;
		startCursor: string;
		endCursor: string;
	}>;
}>;

function makeEmptyData() {
	return {
		edges: [],
		pageInfo: {
			hasPreviousPage: false,
			hasNextPage: true,
			startCursor: '',
			endCursor: '',
		},
	};
}

function cursorFromEdge<TNode>(edges: TInternalRelay<TNode>['edges'], index: number): string {
	// eslint-disable-next-line no-param-reassign
	if (index < 0) index += edges.length;
	const edge = edges[index];
	return edge?.cursor || '';
}

function updateCursor<TNode>(edges: TInternalRelay<TNode>['edges'], index: number, cursor: string | undefined) {
	// eslint-disable-next-line no-param-reassign
	if (index < 0) index += edges.length;
	const edge = edges[index];
	if (cursor && cursor !== edge.cursor) {
		// eslint-disable-next-line no-param-reassign
		edges[index] = { ...edge, cursor };
	}
}

// As proof of the flexibility of field policies, this function generates
// one that handles Relay-style pagination, without Apollo Client knowing
// anything about connections, edges, cursors, or pageInfo objects.

// Manually created as args is submitted as args.input.after instead of args.after
export function cerberusRelayStylePagination<TNode = Reference>(
	keyArgs: KeyArgs = false
): FieldPolicy<TInternalRelay<TNode>> {
	return {
		keyArgs,

		read(existing, { canRead }) {
			if (!existing) return;
			const edges = existing.edges.filter((edge) => canRead(edge.node as StoreValue));
			// eslint-disable-next-line consistent-return
			return {
				// Some implementations return additional Connection fields, such
				// as existing.totalCount. These fields are saved by the merge
				// function, so the read function should also preserve them.
				...existing,
				edges,
				pageInfo: {
					...existing.pageInfo,
					startCursor: cursorFromEdge(edges, 0),
					endCursor: cursorFromEdge(edges, -1),
				},
			};
		},

		merge(existing = makeEmptyData(), incoming, { args }) {
			if (!args) return existing; // TODO Maybe throw?

			const incomingEdges = incoming.edges.slice(0);
			if (incoming.pageInfo) {
				updateCursor(incomingEdges, 0, incoming.pageInfo.startCursor);
				updateCursor(incomingEdges, -1, incoming.pageInfo.endCursor);
			}

			let prefix = existing.edges;
			let suffix: typeof prefix = [];

			if (args.input.after) {
				const index = prefix.findIndex((edge) => edge.cursor === args.input.after);
				if (index >= 0) {
					prefix = prefix.slice(0, index + 1);
					// suffix = []; // already true
				}
			} else if (args.input.before) {
				const index = prefix.findIndex((edge) => edge.cursor === args.input.before);
				suffix = index < 0 ? prefix : prefix.slice(index);
				prefix = [];
			} else {
				// If we have neither args.after nor args.before, the incoming
				// edges cannot be spliced into the existing edges, so they must
				// replace the existing edges. See #6592 for a motivating example.
				prefix = [];
			}

			const edges = [...prefix, ...incomingEdges, ...suffix];

			const pageInfo = {
				...incoming.pageInfo,
				...existing.pageInfo,
				startCursor: cursorFromEdge(edges, 0),
				endCursor: cursorFromEdge(edges, -1),
			};

			const updatePageInfo = (name: keyof TInternalRelay<TNode>['pageInfo']) => {
				const value = incoming.pageInfo[name];
				if (value !== void 0) {
					(pageInfo as any)[name] = value;
				}
			};
			if (!prefix.length) updatePageInfo('hasPreviousPage');
			if (!suffix.length) updatePageInfo('hasNextPage');

			return {
				...existing,
				...incoming,
				edges,
				pageInfo,
			};
		},
	};
}

export default cerberusRelayStylePagination;
